import { ParticleEngine, View, ViewOpts } from '@play-co/timestep-core/ui';
import { SpineView } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import { PetEntity } from 'src/game/logic/EntityManager';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import { playSpineAnimation } from 'src/lib/ui/utils/spine';
import { waitForItPromise } from 'src/lib/utils';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { getRandomFloat } from 'src/replicant/utils/random';
import { rescueLevelThemeFrozen } from '../themes/rescueLevel.theme.frozen';

const HEADER_WIDTH = 720;
const HEADER_HEIGHT = 400;
const SPINE_X = HEADER_WIDTH / 2;
const SPINE_Y = 420;
const PET_PLACEHOLDER_BONE = 'pet';
const PET_PLACEHOLDER_SETUP_SCALE = 0.7790916;
const WIN_PET_HAPPY_DELAY_MS = 4500;
// Let the completion popup cover the final 1.5 seconds of the 9.2-second win animation.
const WIN_POPUP_DELAY_MS = 7700;
const INITIAL_SNOWFLAKE_COUNT = 10;
const SNOWFLAKE_SIZE_MIN = 18;
const SNOWFLAKE_SIZE_MAX = 42;
const SNOWFLAKE_FALL_SPEED_MIN = 50;
const SNOWFLAKE_FALL_SPEED_MAX = 100;
const SNOWFLAKE_DRIFT_MAX = 8;
const SNOWFLAKE_ROTATION_MAX = 0.35;
const SNOWFLAKE_EMIT_INTERVAL_MIN_MS = 100;
const SNOWFLAKE_EMIT_INTERVAL_MAX_MS = 200;

const MAIN_ANIMATIONS = {
  idle: {
    background: 'init_bg',
    foreground: 'init_fg',
  },
  fail: {
    background: 'fail_bg',
    foreground: 'fail_fg',
  },
  win: {
    background: 'win_bg',
    foreground: 'win_fg',
    foreground2: 'win_fg2',
  },
} as const;

const MATCH_OVERLAY_ANIMATIONS = {
  idle: 'matchesoverlay_init',
  lightMatch: 'matchesoverlay_lightmatch',
  lightFire: 'matchesoverlay_lightfire',
} as const;

export class HeaderFrozen extends View {
  private readonly mainBackground: SpineView;
  private readonly mainForeground: SpineView;
  private readonly mainForeground2: SpineView;
  private readonly snowflakeTextures: string[];
  private readonly snowflakes: ParticleEngine;
  private readonly matchesOverlayContainer: View;
  private readonly matchesOverlay: SpineView;
  public readonly pet: PetEntity;
  public tenSecsLeft = false;
  private snowfallActive = false;
  private snowflakeEmitDelayMs = 0;

  constructor(opts: ViewOpts) {
    super({
      ...opts,
      width: HEADER_WIDTH,
      height: HEADER_HEIGHT,
      backgroundColor: '#B0C4FF',
      clip: true,
    });

    this.snowflakeTextures = rescueLevelThemeFrozen.getSnowflakes();

    const sceneRoot = opts.superview ?? this;

    const petScene = new View({
      superview: this,
      scale: DOWNSCALE_FACTOR,
      x: SPINE_X,
      y: SPINE_Y,
    });

    this.mainBackground = new SpineView({
      superview: petScene,
      spinePath: rescueLevelThemeFrozen.getHeaderSpine(),
      x: 0,
      y: 0,
      zIndex: -1,
    });

    this.pet = createUIPet('sad');
    this.pet.updateOpts({
      superview: petScene,
      x: 0,
      y: 0,
      anchorY: 300,
      offsetY: -300,
      zIndex: 0,
    });

    this.mainForeground = new SpineView({
      superview: petScene,
      spinePath: rescueLevelThemeFrozen.getHeaderSpine(),
      x: 0,
      y: 0,
      zIndex: 2,
    });

    this.mainForeground2 = new SpineView({
      superview: petScene,
      spinePath: rescueLevelThemeFrozen.getHeaderSpine(),
      x: 0,
      y: 0,
      visible: false,
      zIndex: 3,
    });

    this.snowflakes = new ParticleEngine({
      superview: petScene,
      width: HEADER_WIDTH,
      height: HEADER_HEIGHT,
      scale: 1 / DOWNSCALE_FACTOR,
      x: -SPINE_X / DOWNSCALE_FACTOR,
      y: -SPINE_Y / DOWNSCALE_FACTOR,
      initCount: 80,
      visible: false,
      zIndex: 1,
    });

    // This overlay must sit outside the header because the match travels
    // into the puzzle area.
    this.matchesOverlayContainer = new View({
      superview: sceneRoot,
      canHandleEvents: false,
      visible: false,
      zIndex: 100,
    });

    this.matchesOverlay = new SpineView({
      superview: this.matchesOverlayContainer,
      spinePath: rescueLevelThemeFrozen.getHeaderSpine(),
      scale: DOWNSCALE_FACTOR,
      x: SPINE_X,
      y: SPINE_Y,
    });

    this.setPetAnimation('cry');
    this.setTick(this.update.bind(this));
    void this.playIdle();
  }

  public async playIdle() {
    this.tenSecsLeft = false;
    this.stopSnowfall();
    this.matchesOverlayContainer.hide();
    this.setPetAnimation('cry');
    this.pet.petClip.pause();
    this.pet.show();

    await this.playMainAnimation(MAIN_ANIMATIONS.idle, true);
  }

  public async playTenSecsLeft() {
    this.tenSecsLeft = true;
    this.startSnowfall();
  }

  public async playRescue() {
    this.stopSnowfall();

    // ResponsiveScene's initial height adjustment can leave root-level views
    // that it does not own with an invalid position.
    this.matchesOverlayContainer.updateOpts({ x: 0, y: 0 });
    this.matchesOverlayContainer.show();
    await playSpineAnimation(this.matchesOverlay, {
      animation: MATCH_OVERLAY_ANIMATIONS.idle,
    });

    await playSpineAnimation(this.matchesOverlay, {
      animation: MATCH_OVERLAY_ANIMATIONS.lightMatch,
    });

    const makePetHappy = (async () => {
      await waitForItPromise(WIN_PET_HAPPY_DELAY_MS);
      this.setPetAnimation('happy');
    })();
    const playWinAnimation = this.playMainAnimation(MAIN_ANIMATIONS.win);

    await Promise.all([
      Promise.race([playWinAnimation, waitForItPromise(WIN_POPUP_DELAY_MS)]),
      playSpineAnimation(this.matchesOverlay, {
        animation: MATCH_OVERLAY_ANIMATIONS.lightFire,
      }),
      makePetHappy,
    ]);

    this.matchesOverlayContainer.hide();
  }

  public async playFail() {
    await this.playMainAnimation(MAIN_ANIMATIONS.fail);
  }

  public async playQuit() {
    void this.playMainAnimation(MAIN_ANIMATIONS.fail);
    await waitForItPromise(1500);
  }

  private async playMainAnimation(
    animation: {
      readonly background: string;
      readonly foreground: string;
      readonly foreground2?: string;
    },
    loop = false,
  ) {
    if (animation.foreground2) {
      this.mainForeground2.show();
    } else {
      this.mainForeground2.hide();
    }

    const animations = [
      playSpineAnimation(this.mainBackground, {
        animation: animation.background,
        loop,
      }),
      playSpineAnimation(this.mainForeground, {
        animation: animation.foreground,
        loop,
      }),
    ];

    if (animation.foreground2) {
      animations.push(
        playSpineAnimation(this.mainForeground2, {
          animation: animation.foreground2,
          loop,
        }),
      );
    }

    try {
      await Promise.all(animations);
    } finally {
      if (animation.foreground2) this.mainForeground2.hide();
    }
  }

  private startSnowfall() {
    if (this.snowfallActive) return;

    this.snowfallActive = true;
    this.snowflakes.show();
    this.snowflakeEmitDelayMs = getRandomFloat(
      SNOWFLAKE_EMIT_INTERVAL_MIN_MS,
      SNOWFLAKE_EMIT_INTERVAL_MAX_MS,
      Math.random,
    );

    for (let i = 0; i < INITIAL_SNOWFLAKE_COUNT; i++) {
      this.emitSnowflake();
    }
  }

  private stopSnowfall() {
    if (!this.snowfallActive) return;

    this.snowfallActive = false;
    this.snowflakeEmitDelayMs = 0;
    this.snowflakes.killAllParticles();
    this.snowflakes.hide();
  }

  private update(dt: number) {
    this.updatePetWithSpine();
    if (!this.snowfallActive) return;

    this.snowflakeEmitDelayMs -= dt;
    if (this.snowflakeEmitDelayMs <= 0) {
      this.emitSnowflake();
      this.snowflakeEmitDelayMs = getRandomFloat(
        SNOWFLAKE_EMIT_INTERVAL_MIN_MS,
        SNOWFLAKE_EMIT_INTERVAL_MAX_MS,
        Math.random,
      );
    }

    this.snowflakes.runTick(dt);
  }

  private emitSnowflake() {
    const particles = this.snowflakes.obtainParticleArray(1);
    const [snowflake] = particles;
    const size = getRandomFloat(
      SNOWFLAKE_SIZE_MIN,
      SNOWFLAKE_SIZE_MAX,
      Math.random,
    );
    const fallSpeed = getRandomFloat(
      SNOWFLAKE_FALL_SPEED_MIN,
      SNOWFLAKE_FALL_SPEED_MAX,
      Math.random,
    );
    const y = -size * Math.SQRT2;

    snowflake.image =
      this.snowflakeTextures[
        Math.floor(Math.random() * this.snowflakeTextures.length)
      ];
    snowflake.x = getRandomFloat(
      -size / 2,
      HEADER_WIDTH - size / 2,
      Math.random,
    );
    snowflake.y = y;
    snowflake.width = size;
    snowflake.height = size;
    snowflake.anchorX = size / 2;
    snowflake.anchorY = size / 2;
    snowflake.r = getRandomFloat(0, Math.PI * 2, Math.random);
    snowflake.dx = getRandomFloat(
      -SNOWFLAKE_DRIFT_MAX,
      SNOWFLAKE_DRIFT_MAX,
      Math.random,
    );
    snowflake.dy = fallSpeed;
    snowflake.dr = getRandomFloat(
      -SNOWFLAKE_ROTATION_MAX,
      SNOWFLAKE_ROTATION_MAX,
      Math.random,
    );
    snowflake.opacity = getRandomFloat(0.65, 0.95, Math.random);
    snowflake.ttl = ((HEADER_HEIGHT - y + size * 2) / fallSpeed) * 1000;

    this.snowflakes.emitParticles(particles);
  }

  private updatePetWithSpine() {
    const bone = this.mainBackground.skeleton.findBone(PET_PLACEHOLDER_BONE);
    if (!bone) return;

    this.pet.updateOpts({
      x: bone.worldX,
      y: bone.worldY,
      r: (bone.getWorldRotationX() * Math.PI) / 180,
      scaleX: bone.getWorldScaleX() / PET_PLACEHOLDER_SETUP_SCALE,
      scaleY: bone.getWorldScaleY() / PET_PLACEHOLDER_SETUP_SCALE,
    });
  }

  private setPetAnimation(state: 'cry' | 'happy') {
    const species = getPet().species ?? 'dog';
    this.pet.setAnimation(
      state === 'cry'
        ? petAnimationNames[species].cry
        : petAnimationNames[species].happy.idle,
    );
  }
}
